import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money, num, round2 } from '../../../utils/nurseryMoney';
import {
  CREDIT_REASON_LABELS, MANUAL_CREDIT_REASONS, MIN_EXPLANATION_CHARS, MIN_EXPLANATION_WORDS, explanationIsEnough,
  type ChosenCreditLine, type CreditableLine, type CreditReason,
} from '../../../types/nurseryAccounts';

/**
 * Raise a credit note against an authorised invoice, or ask for one. Each line
 * is chosen from the invoice (Rob, 2026-10-13): trees by how many, or an
 * amount against a line, such as the delivery or a price correction. Nothing
 * is typed as a description. The figures here are a preview; the server works
 * them out from the invoice line and refuses more than is left to credit.
 *
 * `request` is for a nursery manager (or an Accounts User): nothing is
 * credited until an org admin or an Accounts Admin approves it, so the
 * explanation is required and has to be something they can approve on.
 */

const GST_RATE = 0.1;

interface Line {
  key: number;
  /** An invoice line id, or '' until one is chosen. */
  lineId: number | '';
  /** A tree line credited as an amount (a price correction) instead of by count. */
  byAmount: boolean;
  quantity: string;
  amount: string;
}

const blankLine = (key: number): Line => ({ key, lineId: '', byAmount: false, quantity: '', amount: '' });

/** What a line credits before GST, and why it cannot, as the server will judge it. */
function previewLine(l: Line, inv: CreditableLine | undefined): { amount: number; problem: 'quantity' | 'amount' | null; ready: boolean } {
  if (!inv) return { amount: 0, problem: null, ready: false };
  if (inv.kind === 'goods' && !l.byAmount) {
    const q = num(l.quantity);
    const left = inv.creditable_quantity ?? 0;
    if (q > left) return { amount: 0, problem: 'quantity', ready: false };
    const amount = q >= left ? inv.creditable_amount : Math.min(round2(inv.unit_amount * q), inv.creditable_amount);
    return { amount: q > 0 ? amount : 0, problem: null, ready: q > 0 };
  }
  const amount = round2(num(l.amount));
  if (amount > inv.creditable_amount + 0.004) return { amount: 0, problem: 'amount', ready: false };
  return { amount, problem: null, ready: amount > 0 };
}

export interface CreditNoteDialogProps {
  invoice: { id: number; invoice_number: string; balance: number | string };
  onClose: () => void;
  /** Called once the credit note has been raised, or the request sent. */
  onDone: () => void;
  /** 'request' sends it for approval instead of raising it. */
  mode?: 'raise' | 'request';
}

export function CreditNoteDialog({ invoice, onClose, onDone, mode = 'raise' }: CreditNoteDialogProps) {
  const asking = mode === 'request';
  const { t } = useTranslation('nursery', { keyPrefix: 'CreditNoteDialog' });
  const [reason, setReason] = useState<CreditReason>(MANUAL_CREDIT_REASONS[0]);
  const [lines, setLines] = useState<Line[]>([blankLine(1)]);
  const [creditable, setCreditable] = useState<CreditableLine[] | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState(2);
  const [notes, setNotes] = useState('');
  const [explanation, setExplanation] = useState('');
  const [explanationTouched, setExplanationTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    nurseryAccountsService.getCreditableLines(invoice.id)
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) setCreditable(res.data);
        else setLinesError(errorMessage(res, t('linesFailed')));
      })
      .catch((e) => { if (live) setLinesError(errorMessage(e, t('linesFailed'))); });
    return () => { live = false; };
  }, [invoice.id, t]);

  const byId = new Map((creditable ?? []).map((c) => [c.id, c]));
  const open = (creditable ?? []).filter((c) => c.creditable_amount > 0);
  const previews = lines.map((l) => previewLine(l, l.lineId === '' ? undefined : byId.get(l.lineId)));

  const balance = num(invoice.balance);
  const subtotal = round2(previews.reduce((sum, p) => sum + p.amount, 0));
  const gst = round2(lines.reduce((sum, l, i) => {
    const inv = l.lineId === '' ? undefined : byId.get(l.lineId);
    return sum + (inv?.gst ? round2(previews[i].amount * GST_RATE) : 0);
  }, 0));
  const total = round2(subtotal + gst);

  const complete = lines.length > 0 && previews.every((p) => p.ready);
  const overBalance = total > balance + 0.005;
  const explained = !asking || explanationIsEnough(explanation);

  const setLine = (key: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addLine = () => {
    setLines((ls) => [...ls, blankLine(nextKey)]);
    setNextKey((k) => k + 1);
  };

  const choose = (key: number, value: string) => {
    const id = value ? Number(value) : '';
    const inv = id === '' ? undefined : byId.get(id);
    // The delivery is credited as an amount; start it at what is left.
    setLine(key, {
      lineId: id, byAmount: false, quantity: '',
      amount: inv && inv.kind !== 'goods' ? String(inv.creditable_amount) : '',
    });
  };

  const optionLabel = (c: CreditableLine) => (c.kind === 'goods' && c.creditable_quantity != null
    ? t('treesLeft', { description: c.description, count: c.creditable_quantity })
    : t('amountLeft', { description: c.description, amount: money(c.creditable_amount) }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payloadLines: ChosenCreditLine[] = lines.map((l) => {
        const inv = byId.get(l.lineId as number);
        return inv?.kind === 'goods' && !l.byAmount
          ? { invoice_line_id: l.lineId as number, quantity: num(l.quantity) }
          : { invoice_line_id: l.lineId as number, amount: round2(num(l.amount)) };
      });
      const res = asking
        ? await nurseryAccountsService.requestCreditNote({
          invoice_id: invoice.id, reason, explanation: explanation.trim(), lines: payloadLines,
        })
        : await nurseryAccountsService.createCreditNote({
          invoice_id: invoice.id,
          reason,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          lines: payloadLines,
        });
      if (res.success) {
        onDone();
        return;
      }
      setError(errorMessage(res, t(asking ? 'requestFailed' : 'raiseFailed')));
    } catch (e) {
      setError(errorMessage(e, t(asking ? 'requestFailed' : 'raiseFailed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      wide
      title={t(asking ? 'requestTitle' : 'title')}
      subtitle={t('subtitle', { number: invoice.invoice_number })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={!complete || overBalance || !explained || busy}>
            {busy ? t('working') : t(asking ? 'requestConfirm' : 'confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-neutral-700 break-words">
        {t(asking ? 'requestIntro' : 'intro')}
      </p>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}

      <div>
        <label htmlFor="credit-reason" className="block text-sm font-medium text-neutral-700 mb-1">{t('reason')}</label>
        <select id="credit-reason" value={reason} onChange={(e) => setReason(e.target.value as CreditReason)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
          {MANUAL_CREDIT_REASONS.map((r) => <option key={r} value={r}>{t(`creditReason.${r}`, { defaultValue: CREDIT_REASON_LABELS[r] })}</option>)}
        </select>
      </div>

      {linesError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{linesError}</div>}
      {!creditable && !linesError && <p className="text-sm text-neutral-500">{t('loadingLines')}</p>}
      {creditable && open.length === 0 && <p className="text-sm text-neutral-700 break-words">{t('nothingLeft')}</p>}

      {creditable && open.length > 0 && (
        <div className="space-y-3">
          {lines.map((l, i) => {
            const inv = l.lineId === '' ? undefined : byId.get(l.lineId);
            const takenElsewhere = new Set(lines.filter((x) => x.key !== l.key && x.lineId !== '').map((x) => x.lineId));
            const byCount = inv?.kind === 'goods' && !l.byAmount;
            const preview = previews[i];
            return (
              <div key={l.key} className="border border-neutral-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-neutral-500">{t('line', { n: i + 1 })}</span>
                  {lines.length > 1 && (
                    <button type="button" aria-label={t('removeLine', { n: i + 1 })} className="text-red-500 hover:text-red-700 p-1"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div>
                  <label htmlFor={`credit-item-${l.key}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('item')}</label>
                  <select id={`credit-item-${l.key}`} value={l.lineId} onChange={(e) => choose(l.key, e.target.value)}
                    className="w-full max-w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">{t('chooseItem')}</option>
                    {open.filter((c) => !takenElsewhere.has(c.id)).map((c) => (
                      <option key={c.id} value={c.id}>{optionLabel(c)}</option>
                    ))}
                  </select>
                </div>
                {inv && (
                  <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                    {byCount ? (
                      <div className="w-full sm:w-44">
                        <label htmlFor={`credit-qty-${l.key}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('quantity')}</label>
                        <input id={`credit-qty-${l.key}`} type="text" inputMode="numeric" value={l.quantity}
                          onChange={(e) => setLine(l.key, { quantity: e.target.value })}
                          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    ) : (
                      <div className="w-full sm:w-44">
                        <label htmlFor={`credit-amount-${l.key}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('amount')}</label>
                        <input id={`credit-amount-${l.key}`} type="number" min="0" step="0.01" inputMode="decimal" value={l.amount}
                          onChange={(e) => setLine(l.key, { amount: e.target.value })}
                          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    )}
                    <p className="text-sm text-neutral-700 pb-2">{t('lineAmount', { amount: money(preview.amount) })}</p>
                    {inv.kind === 'goods' && (
                      <label htmlFor={`credit-by-amount-${l.key}`} className="inline-flex items-center gap-2 text-sm text-neutral-700 pb-2 basis-full">
                        <input id={`credit-by-amount-${l.key}`} type="checkbox" checked={l.byAmount}
                          onChange={(e) => setLine(l.key, { byAmount: e.target.checked, quantity: '', amount: '' })} />
                        {t('byAmount')}
                      </label>
                    )}
                  </div>
                )}
                {preview.problem === 'quantity' && inv && (
                  <p className="text-sm text-red-700 break-words" role="alert">{t('quantityOver', { count: inv.creditable_quantity ?? 0 })}</p>
                )}
                {preview.problem === 'amount' && inv && (
                  <p className="text-sm text-red-700 break-words" role="alert">{t('amountOver', { amount: money(inv.creditable_amount) })}</p>
                )}
              </div>
            );
          })}
          {lines.length < open.length && (
            <Button variant="outline" size="sm" type="button" onClick={addLine}>
              <Plus className="h-4 w-4 mr-1" /> {t('addLine')}
            </Button>
          )}
        </div>
      )}

      <dl className="bg-neutral-50 rounded-lg px-3 py-2 text-sm space-y-1">
        <div className="flex justify-between gap-3"><dt className="text-neutral-600">{t('subtotal')}</dt><dd>{money(subtotal)}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-neutral-600">{t('gst')}</dt><dd>{money(gst)}</dd></div>
        <div className="flex justify-between gap-3 font-semibold"><dt>{t('total')}</dt><dd>{money(total)}</dd></div>
        <div className="flex justify-between gap-3 pt-1 border-t border-neutral-200">
          <dt className="text-neutral-600">{t('stillOwing')}</dt><dd>{money(balance)}</dd>
        </div>
      </dl>
      {overBalance && (
        <p className="text-sm text-red-700 break-words" role="alert">
          {t('overBalance', { total: money(total), balance: money(balance), excess: money(round2(total - balance)) })}
        </p>
      )}

      {asking ? (
        <div>
          <label htmlFor="credit-explanation" className="block text-sm font-medium text-neutral-700 mb-1">{t('explanation')}</label>
          <p id="credit-explanation-hint" className="text-xs text-neutral-500 mb-1 break-words">{t('explanationHint')}</p>
          <textarea id="credit-explanation" rows={3} value={explanation} maxLength={1000}
            aria-describedby="credit-explanation-hint"
            onChange={(e) => setExplanation(e.target.value)}
            onBlur={() => setExplanationTouched(true)}
            placeholder={t('explanationPlaceholder')}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          {explanationTouched && !explained && (
            <p className="text-sm text-red-700 break-words" role="alert">
              {t('explanationShort', { words: MIN_EXPLANATION_WORDS, chars: MIN_EXPLANATION_CHARS })}
            </p>
          )}
        </div>
      ) : (
        <div>
          <label htmlFor="credit-notes" className="block text-sm font-medium text-neutral-700 mb-1">{t('notes')}</label>
          <textarea id="credit-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      )}
    </Dialog>
  );
}

export default CreditNoteDialog;
